// src/pages/admin/AdminMedals.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    FaTrophy, FaMedal, FaSearch, FaSyncAlt,
    FaExclamationTriangle, FaFilter, FaSort
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const normalizeText = (value) => String(value || '').toLowerCase().trim();

const getEntryMembers = (participant) => {
    if (!participant) return [];

    if (Array.isArray(participant.entry_members)) {
        return participant.entry_members
            .map(member => String(member || '').trim())
            .filter(Boolean);
    }

    if (typeof participant.entry_members === 'string' && participant.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(participant.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return participant.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (participant) => {
    const members = getEntryMembers(participant);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return participant?.name || '-';
};

const getTeamName = (participant) => {
    return participant?.team?.name || participant?.Team?.name || participant?.team_name || '-';
};

const getTeamUuid = (participant) => {
    return participant?.team?.uuid || participant?.Team?.uuid || participant?.team_uuid || '';
};

const getAthleteClass = (participant) => {
    if (!participant) return '-';

    if (participant.competition_type === 'Kyorugi') {
        return participant.weight_class || '-';
    }

    return participant.poomsae_category || '-';
};

const getParticipantType = (participant) => {
    return participant?.type || '-';
};

const getGenderLabel = (gender) => {
    if (gender === 'Lelaki') return 'Putra';
    if (gender === 'Perempuan') return 'Putri';
    if (gender === 'Campuran') return 'Campuran';
    return gender || '-';
};

const AdminMedals = () => {
    const [isLoading, setIsLoading] = useState(false);

    // TAB UTAMA: standings atau input manual
    const [activeTab, setActiveTab] = useState('standings');

    // SUB-TAB KLASEMEN: overall, prestasi, festival
    const [standingsFilter, setStandingsFilter] = useState('overall');

    const [allStandings, setAllStandings] = useState({
        prestasi: [],
        festival: []
    });

    const [standings, setStandings] = useState([]);

    const [participants, setParticipants] = useState([]);
    const [teams, setTeams] = useState([]);

    const [showFilters, setShowFilters] = useState(false);

    const initialFilters = {
        name: '',
        team: '',
        gender: '',
        division: '',
        class: '',
        competition_type: ''
    };

    const [filters, setFilters] = useState(initialFilters);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: 'asc'
    });

    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        action: null
    });

    // ==========================================
    // FETCH DATA
    // ==========================================
    const fetchTeams = async () => {
        try {
            const response = await api.get('/teams');
            setTeams(Array.isArray(response.data) ? response.data : (response.data.result || response.data.data || []));
        } catch (error) {
            console.error(error);
        }
    };

    const fetchStandings = async () => {
        setIsLoading(true);

        try {
            const response = await api.get('/medals/standings');

            setAllStandings({
                prestasi: response.data.standings_prestasi || [],
                festival: response.data.standings_festival || []
            });
        } catch (error) {
            toast.error("Gagal memuat data klasemen.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchParticipants = async () => {
        setIsLoading(true);

        try {
            const response = await api.get('/medals/participants');
            setParticipants(Array.isArray(response.data) ? response.data : (response.data.result || response.data.data || []));
        } catch (error) {
            toast.error("Gagal memuat data atlet.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    useEffect(() => {
        if (activeTab === 'standings') {
            fetchStandings();
        } else {
            fetchParticipants();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // ==========================================
    // LOGIKA KLASEMEN & PENGGABUNGAN OVERALL
    // ==========================================
    useEffect(() => {
        let sourceData = [];

        if (standingsFilter === 'prestasi') {
            sourceData = [...allStandings.prestasi];
        } else if (standingsFilter === 'festival') {
            sourceData = [...allStandings.festival];
        } else {
            const combinedMap = {};

            [...allStandings.prestasi, ...allStandings.festival].forEach(item => {
                const team = item.team_name || 'TANPA TIM';

                if (!combinedMap[team]) {
                    combinedMap[team] = {
                        team_name: team,
                        gold: 0,
                        silver: 0,
                        bronze: 0
                    };
                }

                combinedMap[team].gold += parseInt(item.gold || 0, 10);
                combinedMap[team].silver += parseInt(item.silver || 0, 10);
                combinedMap[team].bronze += parseInt(item.bronze || 0, 10);
            });

            sourceData = Object.values(combinedMap);
        }

        const formattedData = sourceData
            .map(item => ({
                team: item.team_name,
                gold: parseInt(item.gold || 0, 10),
                silver: parseInt(item.silver || 0, 10),
                bronze: parseInt(item.bronze || 0, 10),
                total:
                    (parseInt(item.gold || 0, 10) * 3) +
                    (parseInt(item.silver || 0, 10) * 2) +
                    parseInt(item.bronze || 0, 10)
            }))
            .filter(item => item.gold > 0 || item.silver > 0 || item.bronze > 0)
            .sort((a, b) =>
                b.gold - a.gold ||
                b.silver - a.silver ||
                b.bronze - a.bronze ||
                a.team.localeCompare(b.team)
            );

        setStandings(formattedData);
    }, [standingsFilter, allStandings]);

    // ==========================================
    // SINKRONISASI MEDALI
    // ==========================================
    const handleSyncMedals = () => {
        setConfirmModal({
            show: true,
            title: 'Sinkronisasi Medali Otomatis',
            message: 'Tindakan ini akan mereset dan menghitung ulang seluruh medali berdasarkan bagan Kyorugi yang selesai dan skor Poomsae tertinggi. Perubahan manual mungkin akan tertimpa. Yakin?',
            action: async () => {
                setConfirmModal({
                    show: false,
                    title: '',
                    message: '',
                    action: null
                });

                const toastId = toast.loading("Mensinkronisasi medali...");

                try {
                    await api.post('/medals/sync');

                    toast.update(toastId, {
                        render: "Sinkronisasi berhasil!",
                        type: "success",
                        isLoading: false,
                        autoClose: 3000
                    });

                    if (activeTab === 'standings') {
                        fetchStandings();
                    } else {
                        fetchParticipants();
                    }
                } catch (error) {
                    toast.update(toastId, {
                        render: "Gagal melakukan sinkronisasi.",
                        type: "error",
                        isLoading: false,
                        autoClose: 3000
                    });
                }
            }
        });
    };

    // ==========================================
    // FILTER & SORT
    // ==========================================
    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setSortConfig({
            key: 'name',
            direction: 'asc'
        });
    };

    const requestSort = (key) => {
        let direction = 'asc';

        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        setSortConfig({
            key,
            direction
        });
    };

    const uniqueDivisions = useMemo(() => {
        return [...new Set(participants.map(p => p.division).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b));
    }, [participants]);

    const sortedParticipants = useMemo(() => {
        let items = [...participants];

        if (standingsFilter !== 'overall') {
            items = items.filter(p => normalizeText(p.type) === standingsFilter);
        }

        items = items.filter(p => {
            const displayName = getDisplayName(p);
            const membersText = getEntryMembers(p).join(' ');
            const teamName = getTeamName(p);
            const athleteClass = getAthleteClass(p);

            const searchText = [
                displayName,
                membersText,
                teamName,
                p.name,
                p.gender,
                p.division,
                p.competition_type,
                p.poomsae_category,
                p.weight_class,
                p.type
            ].join(' ').toLowerCase();

            return (
                (filters.name === '' || searchText.includes(filters.name.toLowerCase())) &&
                (filters.team === '' || getTeamUuid(p) === filters.team) &&
                (filters.gender === '' || p.gender === filters.gender) &&
                (filters.division === '' || p.division === filters.division) &&
                (filters.competition_type === '' || p.competition_type === filters.competition_type) &&
                (filters.class === '' || normalizeText(athleteClass).includes(normalizeText(filters.class)))
            );
        });

        items.sort((a, b) => {
            let aVal = '';
            let bVal = '';

            if (sortConfig.key === 'name') {
                aVal = getDisplayName(a).toLowerCase();
                bVal = getDisplayName(b).toLowerCase();
            } else if (sortConfig.key === 'team') {
                aVal = getTeamName(a).toLowerCase();
                bVal = getTeamName(b).toLowerCase();
            } else if (sortConfig.key === 'class') {
                aVal = getAthleteClass(a).toLowerCase();
                bVal = getAthleteClass(b).toLowerCase();
            } else if (sortConfig.key === 'type') {
                aVal = getParticipantType(a).toLowerCase();
                bVal = getParticipantType(b).toLowerCase();
            } else {
                aVal = a[sortConfig.key]?.toString().toLowerCase() || '';
                bVal = b[sortConfig.key]?.toString().toLowerCase() || '';
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;

            return 0;
        });

        return items;
    }, [participants, filters, sortConfig, standingsFilter]);

    // ==========================================
    // RENDER TAB KLASEMEN
    // ==========================================
    const renderStandings = () => (
        <div className="card border-0 shadow-sm rounded-4 animate-fade-in bg-white mb-4">
            <div className="p-4 p-md-5 border-bottom">
                <h4 className="fw-black text-dark text-uppercase d-flex align-items-center mb-4">
                    <FaTrophy className="me-3 text-warning" /> Klasemen Perolehan Medali
                </h4>

                <div className="d-flex flex-wrap gap-3">
                    <button
                        onClick={() => setStandingsFilter('overall')}
                        className={`btn rounded-4 px-4 py-2 fw-black text-uppercase shadow-sm ${standingsFilter === 'overall' ? 'btn-primary shadow-lg' : 'btn-light text-secondary border'}`}
                        style={{ fontSize: '0.8rem' }}
                    >
                        Klasemen Umum (Semua)
                    </button>

                    <button
                        onClick={() => setStandingsFilter('prestasi')}
                        className={`btn rounded-4 px-4 py-2 fw-black text-uppercase shadow-sm ${standingsFilter === 'prestasi' ? 'btn-info text-white shadow-lg' : 'btn-light text-secondary border'}`}
                        style={{
                            fontSize: '0.8rem',
                            backgroundColor: standingsFilter === 'prestasi' ? '#2563eb' : '',
                            borderColor: standingsFilter === 'prestasi' ? '#2563eb' : ''
                        }}
                    >
                        Khusus Prestasi
                    </button>

                    <button
                        onClick={() => setStandingsFilter('festival')}
                        className={`btn rounded-4 px-4 py-2 fw-black text-uppercase shadow-sm ${standingsFilter === 'festival' ? 'btn-danger text-white shadow-lg' : 'btn-light text-secondary border'}`}
                        style={{
                            fontSize: '0.8rem',
                            backgroundColor: standingsFilter === 'festival' ? '#db2777' : '',
                            borderColor: standingsFilter === 'festival' ? '#db2777' : ''
                        }}
                    >
                        Khusus Festival
                    </button>
                </div>
            </div>

            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table align-middle mb-0 text-nowrap">
                        <thead style={{ backgroundColor: '#1e293b', color: 'white' }}>
                            <tr className="text-uppercase fw-black" style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}>
                                <th className="p-3 text-center" width="10%">Rank</th>
                                <th className="p-3">Klub / Tim Kontingen</th>
                                <th className="p-3 text-center" style={{ backgroundColor: 'rgba(202, 138, 4, 0.2)', color: '#facc15', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>Emas</th>
                                <th className="p-3 text-center" style={{ backgroundColor: 'rgba(148, 163, 184, 0.2)', color: '#cbd5e1', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>Perak</th>
                                <th className="p-3 text-center" style={{ backgroundColor: 'rgba(234, 88, 12, 0.2)', color: '#fb923c', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>Perunggu</th>
                                <th className="p-3 text-center" style={{ backgroundColor: 'rgba(79, 70, 229, 0.3)', color: '#a5b4fc', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>Total Poin</th>
                            </tr>
                        </thead>

                        <tbody className="border-top-0">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="spinner-border text-primary"></div>
                                    </td>
                                </tr>
                            ) : standings.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-5 text-center text-muted fw-bold">
                                        Belum ada perolehan medali yang direkap pada kategori ini.
                                    </td>
                                </tr>
                            ) : (
                                standings.map((row, i) => (
                                    <tr key={row.team} className="hover-bg-light border-bottom">
                                        <td className="p-3 text-center border-0">
                                            {i === 0 ? (
                                                <span className="badge rounded-circle shadow-sm d-inline-flex justify-content-center align-items-center" style={{ width: '28px', height: '28px', backgroundColor: '#facc15', color: '#713f12', fontSize: '14px' }}>1</span>
                                            ) : i === 1 ? (
                                                <span className="badge rounded-circle shadow-sm d-inline-flex justify-content-center align-items-center" style={{ width: '28px', height: '28px', backgroundColor: '#cbd5e1', color: '#334155', fontSize: '14px' }}>2</span>
                                            ) : i === 2 ? (
                                                <span className="badge rounded-circle shadow-sm d-inline-flex justify-content-center align-items-center" style={{ width: '28px', height: '28px', backgroundColor: '#fdba74', color: '#7c2d12', fontSize: '14px' }}>3</span>
                                            ) : (
                                                <span className="text-muted fw-bold">{i + 1}</span>
                                            )}
                                        </td>

                                        <td className="p-3 fw-bold text-primary fs-6 border-0 text-uppercase">
                                            {row.team}
                                        </td>

                                        <td className="p-3 text-center fw-bold fs-6 border-0" style={{ backgroundColor: '#fefce8' }}>
                                            {row.gold}
                                        </td>

                                        <td className="p-3 text-center fw-bold fs-6 border-0" style={{ backgroundColor: '#f8fafc' }}>
                                            {row.silver}
                                        </td>

                                        <td className="p-3 text-center fw-bold fs-6 border-0" style={{ backgroundColor: '#fff7ed' }}>
                                            {row.bronze}
                                        </td>

                                        <td className="p-3 text-center fw-black fs-5 border-0" style={{ backgroundColor: '#eef2ff', color: '#4f46e5' }}>
                                            {row.total}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    // ==========================================
    // RENDER TAB MANUAL INPUT MEDALI
    // ==========================================
    const renderManualInput = () => (
        <div className="card border-0 shadow-sm rounded-4 animate-fade-in bg-white mb-4">
            <div className="card-header bg-white p-4 border-bottom">
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                    <div>
                        <h5 className="fw-black m-0 text-dark text-uppercase tracking-wider">
                            Pemberian Medali Manual
                        </h5>
                        <p className="text-muted m-0 small fw-bold mt-1">
                            Cari atlet atau entry dan tetapkan perolehan medalinya.
                        </p>
                    </div>

                    <div className="d-flex gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-light border rounded-pill px-4 py-2 fw-bold shadow-sm d-flex align-items-center gap-2"
                            onClick={resetFilters}
                        >
                            Reset
                        </button>

                        <button
                            type="button"
                            className={`btn rounded-pill px-4 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 ${showFilters ? 'btn-primary' : 'btn-light border'}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FaFilter /> {showFilters ? 'Tutup Filter' : 'Filter Pencarian'}
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="pt-4 mt-3 border-top animate-fade-in">
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label text-muted fw-bold mb-1 small text-uppercase">
                                    Cari Nama / Entry / Tim
                                </label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-light border-0">
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control bg-light border-0 px-3 py-2 fw-bold"
                                        placeholder="Ketikan nama..."
                                        value={filters.name}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label text-muted fw-bold mb-1 small text-uppercase">
                                    Tim / Kontingen
                                </label>
                                <select
                                    name="team"
                                    className="form-select form-select-sm bg-light border-0 px-3 py-2 fw-bold"
                                    value={filters.team}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Semua Tim</option>
                                    {teams.map(team => (
                                        <option key={team.uuid} value={team.uuid}>
                                            {team.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-2">
                                <label className="form-label text-muted fw-bold mb-1 small text-uppercase">
                                    Gender
                                </label>
                                <select
                                    name="gender"
                                    className="form-select form-select-sm bg-light border-0 px-3 py-2 fw-bold"
                                    value={filters.gender}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Semua</option>
                                    <option value="Lelaki">Putra</option>
                                    <option value="Perempuan">Putri</option>
                                    <option value="Campuran">Campuran</option>
                                </select>
                            </div>

                            <div className="col-md-2">
                                <label className="form-label text-muted fw-bold mb-1 small text-uppercase">
                                    Tipe Lomba
                                </label>
                                <select
                                    name="competition_type"
                                    className="form-select form-select-sm bg-light border-0 px-3 py-2 fw-bold"
                                    value={filters.competition_type}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Semua</option>
                                    <option value="Kyorugi">Kyorugi</option>
                                    <option value="Poomsae">Poomsae</option>
                                </select>
                            </div>

                            <div className="col-md-2">
                                <label className="form-label text-muted fw-bold mb-1 small text-uppercase">
                                    Divisi
                                </label>
                                <select
                                    name="division"
                                    className="form-select form-select-sm bg-light border-0 px-3 py-2 fw-bold"
                                    value={filters.division}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Semua</option>
                                    {uniqueDivisions.map(division => (
                                        <option key={division} value={division}>
                                            {division}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label text-muted fw-bold mb-1 small text-uppercase">
                                    Kelas / Kategori
                                </label>
                                <input
                                    type="text"
                                    name="class"
                                    className="form-control form-control-sm bg-light border-0 px-3 py-2 fw-bold"
                                    placeholder="Contoh: U-45 / Freestyle / Pair"
                                    value={filters.class}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="card-body p-0">
                <div className="table-responsive custom-scrollbar" style={{ maxHeight: '600px' }}>
                    <table className="table table-hover align-middle mb-0">
                        <thead className="sticky-top" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                            <tr className="text-uppercase fw-black" style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}>
                                <th className="p-3 cursor-pointer" onClick={() => requestSort('name')}>
                                    Nama Atlet / Entry {sortConfig.key === 'name' && <FaSort />}
                                </th>

                                <th className="p-3 cursor-pointer" onClick={() => requestSort('team')}>
                                    Klub / Tim {sortConfig.key === 'team' && <FaSort />}
                                </th>

                                <th className="p-3 cursor-pointer" onClick={() => requestSort('type')}>
                                    Tipe
                                </th>

                                <th className="p-3 cursor-pointer" onClick={() => requestSort('class')}>
                                    Kategori & Kelas {sortConfig.key === 'class' && <FaSort />}
                                </th>

                                <th className="p-3 text-center">
                                    Aksi Input Medali
                                </th>
                            </tr>
                        </thead>

                        <tbody className="border-top-0">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5">
                                        <div className="spinner-border text-primary"></div>
                                    </td>
                                </tr>
                            ) : sortedParticipants.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5 text-muted fw-bold">
                                        Tidak ada data atlet / entry yang sesuai filter.
                                    </td>
                                </tr>
                            ) : (
                                sortedParticipants.map((participant) => {
                                    const members = getEntryMembers(participant);
                                    const displayName = getDisplayName(participant);
                                    const teamName = getTeamName(participant);
                                    const athleteClass = getAthleteClass(participant);

                                    return (
                                        <tr key={participant.uuid} className="border-bottom">
                                            <td className="p-3">
                                                <div className="fw-black text-dark text-uppercase">
                                                    {displayName}
                                                </div>

                                                <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                                                    <span className="small text-muted fw-bold">
                                                        {getGenderLabel(participant.gender)}
                                                    </span>

                                                    {members.length > 1 && (
                                                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">
                                                            {members.length} Nama / 1 Entry
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="p-3 fw-bold text-muted small text-uppercase">
                                                {teamName}
                                            </td>

                                            <td className="p-3">
                                                <span className={`badge px-3 py-2 rounded-pill ${
                                                    normalizeText(participant.type) === 'prestasi'
                                                        ? 'bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25'
                                                        : 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25'
                                                }`}>
                                                    {participant.type || '-'}
                                                </span>
                                            </td>

                                            <td className="p-3">
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <span className="badge bg-light text-dark border px-2 py-1">
                                                        {participant.competition_type || '-'}
                                                    </span>

                                                    <span className="fw-bold small">
                                                        {athleteClass}
                                                    </span>

                                                    {participant.division && (
                                                        <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25">
                                                            {participant.division}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="p-3 text-center">
                                                <select
                                                    className={`form-select form-select-sm fw-bold shadow-none rounded-pill px-3 py-2 mx-auto ${
                                                        participant.medal === 'Gold' ? 'bg-warning bg-opacity-25 border-warning text-dark' :
                                                        participant.medal === 'Silver' ? 'bg-secondary bg-opacity-10 border-secondary text-dark' :
                                                        participant.medal === 'Bronze' ? 'bg-danger bg-opacity-10 border-danger text-danger' :
                                                        participant.medal === 'Participant' ? 'bg-info bg-opacity-10 border-info text-info' :
                                                        'bg-light border-0 text-muted'
                                                    }`}
                                                    style={{
                                                        maxWidth: '170px',
                                                        cursor: 'pointer'
                                                    }}
                                                    value={participant.medal || ''}
                                                    onChange={async (e) => {
                                                        const val = e.target.value;

                                                        try {
                                                            await api.patch(`/medals/${participant.uuid}`, {
                                                                medal: val
                                                            });

                                                            setParticipants(prev => prev.map(item =>
                                                                item.uuid === participant.uuid
                                                                    ? { ...item, medal: val }
                                                                    : item
                                                            ));

                                                            toast.success("Medali berhasil diupdate!");
                                                        } catch (err) {
                                                            toast.error("Gagal update medali.");
                                                        }
                                                    }}
                                                >
                                                    <option value="">-- Set Medali --</option>
                                                    <option value="Gold">🥇 EMAS</option>
                                                    <option value="Silver">🥈 PERAK</option>
                                                    <option value="Bronze">🥉 PERUNGGU</option>
                                                    <option value="Participant">🏅 PESERTA</option>
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="container-fluid p-0 d-flex flex-column h-100 position-relative" style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingBottom: '50px' }}>

            {/* HEADER UTAMA */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3 p-4 pb-0">
                <div>
                    <h3 className="fw-black text-dark mb-1 text-uppercase tracking-wider">
                        Management Medali
                    </h3>
                    <p className="text-muted m-0 small fw-bold">
                        Sinkronisasi otomatis atau input manual perolehan medali atlet.
                    </p>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                    <button
                        className="btn btn-warning rounded-pill px-4 py-2 fw-black shadow-sm d-flex align-items-center gap-2 text-dark"
                        onClick={handleSyncMedals}
                    >
                        <FaSyncAlt /> Hitung Otomatis (Sync)
                    </button>
                </div>
            </div>

            <div className="px-4 mb-3">
                <div className="bg-white rounded-pill p-1 border shadow-sm d-inline-flex gap-1">
                    <button
                        className={`btn rounded-pill px-4 py-2 fw-black text-uppercase ${activeTab === 'standings' ? 'btn-primary shadow-sm' : 'btn-white text-muted'}`}
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => setActiveTab('standings')}
                    >
                        Lihat Klasemen
                    </button>

                    <button
                        className={`btn rounded-pill px-4 py-2 fw-black text-uppercase ${activeTab === 'input' ? 'btn-primary shadow-sm' : 'btn-white text-muted'}`}
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => setActiveTab('input')}
                    >
                        Input Manual
                    </button>
                </div>
            </div>

            <div className="px-4 flex-grow-1">
                {activeTab === 'standings' ? renderStandings() : renderManualInput()}
            </div>

            {/* MODAL CONFIRMATION */}
            {confirmModal.show && createPortal(
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        zIndex: 1200,
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(3px)'
                    }}
                >
                    <div className="card border-0 shadow-lg rounded-4" style={{ width: '90%', maxWidth: '400px' }}>
                        <div className="card-body p-5 text-center">
                            <div className="mb-3">
                                <div className="bg-warning bg-opacity-10 text-warning rounded-circle d-inline-flex p-4 shadow-sm mb-2">
                                    <FaExclamationTriangle size={40} />
                                </div>
                            </div>

                            <h5 className="fw-black text-dark mb-2">
                                {confirmModal.title}
                            </h5>

                            <p className="text-muted mb-4 small">
                                {confirmModal.message}
                            </p>

                            <div className="d-flex gap-2 justify-content-center">
                                <button
                                    className="btn btn-light border rounded-pill px-4 fw-bold"
                                    onClick={() => setConfirmModal({
                                        show: false,
                                        title: '',
                                        message: '',
                                        action: null
                                    })}
                                >
                                    Batal
                                </button>

                                <button
                                    className="btn btn-warning rounded-pill fw-bold text-dark px-4 shadow-sm"
                                    onClick={confirmModal.action}
                                >
                                    Ya, Sinkronisasi
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* CSS STYLE KHUSUS HALAMAN INI */}
            <style>{`
                .tracking-wider {
                    letter-spacing: 1px;
                }

                .cursor-pointer {
                    cursor: pointer;
                }

                .hover-bg-light:hover {
                    background-color: #f8fafc !important;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }

                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 10px;
                }

                .animate-fade-in {
                    animation: fadeIn 0.3s ease-out forwards;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(8px);
                    }

                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminMedals;